import { cockpitExampleQuestion } from './scenario-cockpit-panels';
import { QuestionKind, QuestionKindInfo, Scenario, ScenarioQuestion, ScenarioStarter } from './scenario.model';

export const QUESTION_KINDS: QuestionKindInfo[] = [
  { kind: 'multiple-choice', group: 'A', name: 'Multiple choice', note: 'Straight from the quiz bank, one correct answer.', eyebrow: 'Quiz question', color: 'var(--purple-solid)' },
  { kind: 'multiple-response', group: 'A', name: 'Multiple response', note: 'Select all that apply, scored on partial credit.', eyebrow: 'Multiple response · select all that apply', color: 'var(--purple-solid)' },
  { kind: 'true-false-round', group: 'A', name: 'True/false rapid round', note: 'A run of short calls under one shared stem.', eyebrow: 'True / false · rapid round', color: 'var(--purple-solid)' },
  { kind: 'cloze', group: 'A', name: 'Cloze — fill in the blank', note: 'Drag a term to a blank, or tap both.', eyebrow: 'Cloze · fill in the blanks', color: 'var(--info-solid)' },
  { kind: 'answer-rationale', group: 'A', name: 'Two-step answer + rationale', note: 'Commit to an answer, then justify it.', eyebrow: 'Two-step · answer, then rationale', color: 'var(--dw-navbar-bg)' },
  { kind: 'confidence', group: 'A', name: 'Confidence-rated answer', note: 'Answer plus a confidence stake; wrong-and-certain is flagged.', eyebrow: 'Confidence-rated answer', color: 'var(--purple-solid)' },
  { kind: 'sequencing', group: 'B', name: 'Sequencing / ordering', note: 'Put steps into the correct order.', eyebrow: 'Sequencing · single ranked column', color: 'var(--dw-navbar-bg)' },
  { kind: 'matching', group: 'B', name: 'Matching pairs', note: 'Drag items to their matches, or tap both.', eyebrow: 'Matching pairs', color: 'var(--info-solid)' },
  { kind: 'numeric', group: 'B', name: 'Numeric entry with tolerance', note: 'Exact figure accepted within a band.', eyebrow: 'Numeric entry · graded to tolerance', color: 'var(--dw-navbar-bg)' },
  { kind: 'slider', group: 'B', name: 'Slider / dial estimate', note: 'Estimate on a continuous scale.', eyebrow: 'Slider · set the control', color: 'var(--info-solid)' },
  { kind: 'timed', group: 'B', name: 'Timed decision', note: 'A countdown forces the call.', eyebrow: 'Timed decision', color: 'var(--danger-solid)' },
  { kind: 'ranked', group: 'B', name: 'Ranked priority', note: 'Order actions by what comes first.', eyebrow: 'Ranked priority', color: 'var(--purple-solid)' },
  { kind: 'panel-read', group: 'C', name: 'Instrument panel read', note: 'Read a live PFD state and say what the aircraft is doing.', eyebrow: 'Instrument panel read', color: 'var(--info-solid)' },
  { kind: 'chart-lookup', group: 'C', name: 'Chart / plate lookup', note: 'Zoom and pan a plate, answer off the chart.', eyebrow: 'Chart / plate lookup', color: 'var(--dw-navbar-bg)' },
  { kind: 'cockpit-control', group: 'C', name: 'Cockpit control click', note: 'One click on the panel; a wrong control runs its consequence.', eyebrow: 'Cockpit control click', color: 'var(--warning-solid-hover)' },
  { kind: 'radio-phraseology', group: 'C', name: 'Radio phraseology builder', note: 'Drag or tap phrases into a transmission, scored on required elements.', eyebrow: 'Radio phraseology builder', color: 'var(--purple-solid)' },
  { kind: 'audio-cue', group: 'C', name: 'Audio-cued question', note: 'ATIS or ATC read aloud. Two plays, no transcript.', eyebrow: 'Audio-cued question', color: 'var(--info-solid)' },
  { kind: 'hazard-audit', group: 'C', name: 'Multi-hotspot audit', note: 'Tag every hazard; found, missed, and false positives count.', eyebrow: 'Multi-hotspot audit', color: 'var(--danger-solid)' },
  { kind: 'diversion-plot', group: 'C', name: 'Diversion plot', note: 'Drag the route to an alternate that works for weather, fuel, and services.', eyebrow: 'Diversion plot', color: 'var(--warning-solid-hover)' },
];

export const KIND_GROUPS = [
  { tag: 'A', name: 'Quiz formats', note: 'Familiar questions and answer patterns' },
  { tag: 'B', name: 'Interactive exercises', note: 'Ordering, matching, and estimating' },
  { tag: 'C', name: 'Aviation scenarios', note: 'Instruments, charts, and cockpit decisions' },
];

export function kindInfo(kind: QuestionKind): QuestionKindInfo {
  return QUESTION_KINDS.find(k => k.kind === kind) || QUESTION_KINDS[0];
}

/** Sample content for every kind (from the design library). New steps start from a copy of these. */
export const SAMPLE_QUESTIONS: Record<QuestionKind, ScenarioQuestion> = {
  'multiple-choice': {
    kind: 'multiple-choice', points: 5, title: 'What does the group 19012G18KT tell you about the surface wind?',
    choices: [
      { text: 'Wind from 190° true at 12 kt, gusting 18 kt', correct: true },
      { text: 'Wind from 190° magnetic at 18 kt sustained' },
      { text: 'Runway 19, 12 to 18 kt crosswind component' },
    ],
    explanation: 'Read it as ddd ff G gg KT: direction (true), speed, gusts.',
  },
  'multiple-response': {
    kind: 'multiple-response', points: 4,
    title: 'Which items are required for IFR flight in controlled airspace under 14 CFR 91.205(d)?',
    prompt: 'Partial credit applies — each correct pick earns, each wrong pick costs.',
    choices: [
      { text: 'Generator or alternator of adequate capacity', correct: true },
      { text: 'Slip-skid indicator', correct: true },
      { text: 'Sensitive altimeter adjustable for barometric pressure', correct: true },
      { text: 'Clock with a sweep-second pointer or digital presentation', correct: true },
      { text: 'Landing light' },
      { text: 'Anti-collision light system' },
    ],
    explanation: 'Landing lights are only required for night operations for hire, and anti-collision lights are an installation rule, not an IFR requirement.',
  },
  'true-false-round': {
    kind: 'true-false-round', points: 6, title: 'Six quick calls on regulations and limitations',
    prompt: 'One step, six statements. Answer without deliberating.',
    statements: [
      { text: 'VFR entry into Class E airspace requires an ATC clearance.', answer: false },
      { text: 'Below 10,000 ft MSL the maximum indicated airspeed is 250 knots.', answer: true },
      { text: 'VFR cruising altitudes apply more than 3,000 ft above the surface.', answer: true },
      { text: 'A Mode C transponder is required within 30 NM of a Class B primary airport.', answer: true },
      { text: 'The crew must use supplemental oxygen the moment the cabin passes 12,500 ft MSL.', answer: false },
      { text: 'Maneuvering speed decreases as gross weight decreases.', answer: true },
    ],
    explanation: 'Class E needs no clearance for VFR, and crew oxygen starts after 30 minutes above 12,500 ft MSL.',
  },
  'cloze': {
    kind: 'cloze', points: 3, title: 'Decode the KLBB observation',
    prompt: 'Drag each term to a blank, or tap a term and then its blank.',
    context: 'KLBB 221753Z 19012G18KT 10SM SCT045 BKN250 31/12 A2992',
    clozeParts: ['The surface wind is from', 0, 'at 12 knots gusting 18, visibility is', 1, ', and the lowest layer is', 2, 'at 4,500 ft AGL.'],
    terms: ['190° true', '190° magnetic', '10 statute miles', '10 kilometers', 'scattered', 'broken'],
    clozeAnswers: [0, 2, 4],
    explanation: 'METAR wind is true, US visibility is statute miles, and SCT045 is the scattered layer at 4,500 ft.',
  },
  'answer-rationale': {
    kind: 'answer-rationale', points: 10, title: 'Engine failure at 600 ft AGL',
    prompt: 'Pick the action, then pick why. A lucky guess on step 1 still has to survive step 2.',
    choices: [
      { text: 'Turn back to runway 17R' },
      { text: 'Pitch for 68 KIAS and land in the field ahead', correct: true },
      { text: 'Hold altitude and troubleshoot the fuel selector' },
    ],
    whys: [
      { text: 'The field is longer than the runway remaining' },
      { text: 'Below about 1,000 ft AGL the 180° return costs more altitude than you have', correct: true },
      { text: 'Company procedure requires landing ahead below 1,000 ft' },
    ],
    explanation: 'The reason is energy: a 180° return from 600 ft costs more altitude than you have.',
  },
  'confidence': {
    kind: 'confidence', points: 5,
    title: 'Density altitude is 5,600 ft. What happens on the takeoff roll at near max gross weight?',
    prompt: 'Answer, then rate how sure you are.',
    choices: [
      { text: 'Ground roll increases by roughly 25% and climb rate drops', correct: true },
      { text: 'Ground roll is unchanged; only climb rate drops' },
      { text: 'Ground roll decreases because true airspeed is higher' },
    ],
    explanation: 'Thinner air means less thrust and less lift at the same indicated airspeed.',
  },
  'sequencing': {
    kind: 'sequencing', points: 5, title: 'Put the emergency memory items in the order you would fly them',
    prompt: 'Reorder top to bottom.',
    items: ['Pitch for 68 KIAS — best glide', 'Pick the landing area ahead', 'Fuel selector BOTH, mixture rich', 'Carb heat on, ignition check', 'Squawk 7700 · declare emergency'],
    explanation: 'Airspeed first, landing area second, restart attempt, then the radio. Aviate, navigate, communicate.',
  },
  'matching': {
    kind: 'matching', points: 4, title: 'Match each light gun signal to its meaning in flight',
    prompt: 'Drag a signal to its meaning, or tap both.',
    pairs: [
      { left: 'Steady green', right: 'Cleared to land', dot: '#3d9a5f' },
      { left: 'Flashing green', right: 'Return for landing', dot: '#7fd39a' },
      { left: 'Steady red', right: 'Give way to other aircraft and continue circling', dot: '#c44545' },
      { left: 'Flashing red', right: 'Airport unsafe — do not land', dot: '#e08b8b' },
    ],
    explanation: 'Steady green lands, flashing green returns, steady red circles, flashing red means unsafe.',
  },
  'numeric': {
    kind: 'numeric', points: 5, title: 'What is the crosswind component for the departure?',
    prompt: 'Graded inside a band so rounding is not punished.',
    context: 'Runway 17R · wind 190° at 12 kt', answer: 4.1, tolerance: 1, unit: 'knots',
    explanation: '20° off the runway: 12 × sin 20° = 4.1 kt of crosswind, 11.3 kt down the runway.',
  },
  'slider': {
    kind: 'slider', points: 3, title: 'Set the heading bug for the departure runway',
    prompt: 'Drag to a value instead of picking from a list.', answer: 170, tolerance: 5, min: 0, max: 359, unit: '°',
    explanation: 'Runway 17R sits on about 170° magnetic. Anything inside 165–175 counts.',
  },
  'timed': {
    kind: 'timed', points: 10, title: 'The engine quits at 600 ft AGL. Act.',
    prompt: 'Let the clock run out and the scenario branches to "no action taken".', seconds: 12,
    choices: [
      { text: 'Turn back to runway 17R' },
      { text: 'Best glide, land in the field ahead', correct: true },
      { text: 'Attempt an air restart while holding altitude' },
    ],
    explanation: 'Best glide and a landing area within about 30° of the nose. The alternatives spend altitude you cannot get back.',
  },
  'ranked': {
    kind: 'ranked', points: 4, title: 'Rank the competing demands in the first 15 seconds',
    prompt: 'All four are real tasks. The learning is in the order.',
    items: ['Establish and hold best glide', 'Select the landing area', 'Run the engine-restart flow', 'Declare on 121.5'],
    explanation: 'Glide first, landing area second, restart flow third, radio last.',
  },
  'panel-read': {
    kind: 'panel-read', points: 5, title: 'Read the panel. What is the aircraft doing right now?',
    prompt: 'A live panel state instead of a sentence describing one.',
    panel: { ias: 118, alt: 2340, vsi: -700, pitch: -4, bank: 12, hdg: 260 },
    choices: [
      { text: 'Level right turn at standard rate' },
      { text: 'Descending right turn, about 700 fpm down', correct: true },
      { text: 'Climbing right turn with the airspeed decaying' },
      { text: 'Wings-level descent on a southwest heading' },
    ],
    explanation: '12° right bank, nose 4° low, 700 fpm down: a descending right turn.',
  },
  'chart-lookup': {
    kind: 'chart-lookup', points: 4, title: 'Find the minimum safe altitude for the KLBB ILS 17R',
    prompt: 'Zoom and pan the plate, then answer off the chart.', answer: 5000, tolerance: 0, unit: 'ft MSL',
    explanation: 'The MSA circle is referenced to the LBB VORTAC and reads 5000 within 25 NM.',
  },
  'cockpit-control': cockpitExampleQuestion(),
  'radio-phraseology': {
    kind: 'radio-phraseology', points: 5, title: 'Build your initial call to Lubbock Approach',
    prompt: 'Assemble the transmission. Graded on required elements, not one script.',
    elements: ['Facility addressed first', 'Full callsign', 'Position', 'Altitude', 'Request stated'],
    phrases: [
      { text: 'Lubbock Approach', element: 0 },
      { text: 'Skyhawk 8-4-2 Charlie Papa', element: 1 },
      { text: 'Cessna', element: -1 },
      { text: 'ten miles south of the field', element: 2 },
      { text: 'two thousand five hundred', element: 3 },
      { text: 'twelve thousand five hundred', element: -1 },
      { text: 'request flight following to Amarillo', element: 4 },
      { text: 'with you', element: -1 },
      { text: 'over', element: -1 },
    ],
    explanation: 'Who you are calling, who you are, where, what altitude, what you want.',
  },
  'audio-cue': {
    kind: 'audio-cue', points: 4, title: 'Which readback matches the broadcast?',
    prompt: 'No transcript, two plays.', plays: 2,
    audioScript: 'Lubbock international airport information Delta. One seven five three zulu. Wind one niner zero at one two, gusts one eight. Visibility one zero. Ceiling one thousand eight hundred broken. Altimeter two niner niner two. Landing and departing runway one seven right. Advise on initial contact you have information Delta.',
    choices: [
      { text: 'Runway 17R in use, altimeter 29.92, ceiling 1,800 broken', correct: true },
      { text: 'Runway 35L in use, altimeter 29.92, ceiling 1,800 overcast' },
      { text: 'Runway 17R in use, altimeter 30.02, ceiling 800 broken' },
      { text: 'Runway 26 in use, altimeter 29.92, few clouds at 4,500' },
    ],
    explanation: 'Delta gave 17R, 29.92, and 1,800 broken. Each distractor changes one item.',
  },
  'hazard-audit': {
    kind: 'hazard-audit', points: 3, title: 'Tag every hazard on this ramp',
    prompt: 'Scored on what was found, what was missed, and false positives.',
    hotspots: [
      { label: 'Chocks still in place', x: 20, y: 72 },
      { label: 'Fuel truck hose across the taxi line', x: 56, y: 26, correct: true },
      { label: 'Loose baggage cart, no brake', x: 78, y: 58, correct: true },
      { label: 'Wingtip inside the marked box', x: 34, y: 42 },
      { label: 'Prop turning with crew forward of the wing', x: 46, y: 84, correct: true },
      { label: 'Tiedown ring unused', x: 86, y: 30 },
    ],
    explanation: 'Hose across the taxi line, unbraked cart, and a turning prop with crew forward are the three.',
  },
  'diversion-plot': {
    kind: 'diversion-plot', points: 10, title: 'The line is not passable. Plot the diversion.',
    prompt: 'Drag the route endpoint to the alternate you would actually fly to.',
    airports: [
      { id: 'KLBB', x: 120, y: 270, note: 'departure', origin: true },
      { id: 'KAMA', x: 300, y: 60, note: 'destination · in the line' },
      { id: 'KPVW', x: 250, y: 150, note: 'in the cells' },
      { id: 'KCDS', x: 600, y: 120, note: 'beyond fuel range' },
      { id: 'KSNK', x: 400, y: 270, note: 'clear · in range · fuel + services' },
    ],
    correctAirport: 'KSNK',
    explanation: 'KSNK is clear of the cells, inside the fuel ring, with fuel and a hard surface.',
  },
};

export function sampleQuestion(kind: QuestionKind): ScenarioQuestion {
  return JSON.parse(JSON.stringify(SAMPLE_QUESTIONS[kind]));
}

export const DEMO_SCENARIO: Scenario = {
  title: 'Engine failure after takeoff — KLBB',
  subtitle: 'Private Pilot · Module 07 — Emergency ops',
  steps: [
    { id: 's1', type: 'narrative', title: 'Preflight briefing', x: 40, y: 260, next: 's2', imageUrl: 'assets/scenarios/lesson-cessna-172s.png',
      body: 'You are pilot in command of N734AP, a Cessna 172S, departing KLBB runway 17R for a VFR cross-country to Amarillo. Density altitude is 5,600 ft and you are near max gross weight.\n\nKLBB 221753Z 19012G18KT 10SM SCT045 BKN250 31/12 A2992' },
    { id: 's2', type: 'question', title: 'Decode the METAR', x: 320, y: 260, next: 's3', question: sampleQuestion('cloze') },
    { id: 's3', type: 'question', title: 'Takeoff performance', x: 600, y: 260, next: 's4', question: sampleQuestion('confidence') },
    { id: 's4', type: 'question', title: 'Engine failure at 600 ft', x: 880, y: 260, onCorrect: 's5', onIncorrect: 'r1', question: sampleQuestion('timed') },
    { id: 's5', type: 'question', title: 'Emergency flow', x: 1180, y: 100, next: 's6', question: sampleQuestion('sequencing') },
    { id: 'r1', type: 'remediation', title: 'The impossible turn', x: 1180, y: 440, next: 's4', imageUrl: 'assets/scenarios/lesson-private-pilot.png',
      body: 'A 180° return to the runway from 600 ft costs more altitude than you have: bank angle, wind, and reaction time eat the margin. Review the energy math, then retry the decision.' },
    { id: 's6', type: 'question', title: 'Why land ahead?', x: 1460, y: 100, next: 's7', question: sampleQuestion('answer-rationale') },
    { id: 's7', type: 'debrief', title: 'Debrief & ADM chain', x: 1740, y: 100, imageUrl: 'assets/scenarios/lesson-private-pilot.png',
      body: 'You land in the plowed field off the departure end with no damage. The debrief walks the ADM chain: density altitude, weight, and the decision you rehearsed before takeoff.' },
  ],
};

/**
 * Smallest branching loop worth starting from: brief, decide, then continue (correct)
 * or remediate and retry (incorrect), ending in a debrief. Same retry shape as DEMO_SCENARIO's r1 -> s4.
 *
 *   brief ──next──▶ decide ──onCorrect──▶ continue ──next──▶ debrief
 *                    ▲  └──onIncorrect──▶ remediate
 *                    └──────────next (retry)──────┘
 */
export const GUIDED_DECISION_SCENARIO: Scenario = {
  title: 'Guided decision',
  subtitle: 'Brief, decide, and get coaching before retrying',
  steps: [
    { id: 'brief', type: 'narrative', title: 'Briefing', x: 40, y: 200, next: 'decide',
      body: 'Set the scene: aircraft, phase of flight, weather, and what the pilot is trying to accomplish.' },
    { id: 'decide', type: 'question', title: 'Decision point', x: 320, y: 200, onCorrect: 'continue', onIncorrect: 'remediate',
      question: {
        kind: 'multiple-choice', points: 5, title: 'What should the pilot do next?',
        choices: [
          { text: 'The safe, correct action', correct: true },
          { text: 'A tempting but unsafe action', why: 'Explain the consequence the coaching will cover.' },
          { text: 'A common misconception', why: 'Name the misconception and the rule it breaks.' },
        ],
        explanation: 'State the rule or reasoning that makes the correct action right.',
      } },
    { id: 'continue', type: 'narrative', title: 'Continue the flight', x: 620, y: 60, next: 'debrief',
      body: 'Show the outcome of the good decision and carry the scenario forward.' },
    { id: 'remediate', type: 'remediation', title: 'Why that choice fails', x: 620, y: 360, next: 'decide',
      body: 'Walk through why the chosen action fails, then send the student back to the decision.' },
    { id: 'debrief', type: 'debrief', title: 'Debrief', x: 900, y: 60,
      body: 'Summarize the decision, the cues that pointed to it, and how it applies on the next flight.' },
  ],
};

/** A linear starting structure with one question and no branching to configure. */
export const BASIC_SCENARIO: Scenario = {
  title: 'Simple scenario',
  subtitle: 'Briefing, one question, and a debrief',
  steps: [
    { id: 'brief', type: 'narrative', title: 'Briefing', x: 40, y: 200, next: 'decide',
      body: 'Describe the situation and what the student needs to decide.' },
    { id: 'decide', type: 'question', title: 'Question', x: 320, y: 200, next: 'debrief',
      question: GUIDED_DECISION_SCENARIO.steps[1].question },
    { id: 'debrief', type: 'debrief', title: 'Debrief', x: 600, y: 200,
      body: 'Explain the answer and the main takeaway.' },
  ],
};

// Presets are supplied starting structures. Saving user work only updates savedFlows, never this catalog.
export const STARTER_FLOWS: ReadonlyArray<ScenarioStarter> = [
  { id: 'simple-scenario', icon: 'fa-list-ol', note: 'Briefing → question → debrief', scenario: BASIC_SCENARIO },
  { id: 'guided-decision', icon: 'fa-code-branch', note: 'Briefing → decision with coaching → debrief', scenario: GUIDED_DECISION_SCENARIO },
];
